export type SocialPlatform = 'linkedin' | 'instagram' | 'facebook' | 'youtube';

export const SOCIAL_PLATFORM_KEYS: SocialPlatform[] = [
  'linkedin',
  'instagram',
  'facebook',
  'youtube',
];

interface PlatformRule {
  domains: string[];
  shortDomains: string[];
  canonicalDomain: string;
  bareUsernamePrefix: string;
}

const SOCIAL_RULES: Record<SocialPlatform, PlatformRule> = {
  instagram: {
    domains: ['instagram.com', 'www.instagram.com'],
    shortDomains: ['instagr.am'],
    canonicalDomain: 'www.instagram.com',
    bareUsernamePrefix: '/',
  },
  linkedin: {
    domains: ['linkedin.com', 'www.linkedin.com'],
    shortDomains: ['lnkd.in'],
    canonicalDomain: 'www.linkedin.com',
    bareUsernamePrefix: '/in/',
  },
  youtube: {
    domains: ['youtube.com', 'www.youtube.com', 'm.youtube.com'],
    shortDomains: ['youtu.be'],
    canonicalDomain: 'www.youtube.com',
    bareUsernamePrefix: '/@',
  },
  facebook: {
    domains: ['facebook.com', 'www.facebook.com', 'm.facebook.com'],
    shortDomains: ['fb.com'],
    canonicalDomain: 'www.facebook.com',
    bareUsernamePrefix: '/',
  },
};

const ALL_PLATFORM_DOMAINS = Object.values(SOCIAL_RULES).flatMap((r) => [
  ...r.domains,
  ...r.shortDomains,
]);

export type SocialValidationResult = { ok: true; url: string } | { ok: false; error: string };

function sanitizeInput(input: string): string {
  return input.trim().replace(/\s+/g, '');
}

function hostnameMatchesPlatform(hostname: string, platform: SocialPlatform): boolean {
  const rule = SOCIAL_RULES[platform];
  const all = [...rule.domains, ...rule.shortDomains];
  return all.some((d) => hostname === d || hostname === `www.${d}`);
}

function findPlatformForHostname(hostname: string): SocialPlatform | null {
  for (const key of SOCIAL_PLATFORM_KEYS) {
    if (hostnameMatchesPlatform(hostname, key)) return key;
  }
  return null;
}

export function normalizeSocialUrl(
  platform: SocialPlatform,
  input: string
): SocialValidationResult {
  const sanitized = sanitizeInput(input);
  if (!sanitized) return { ok: false, error: 'Empty' };

  const lower = sanitized.toLowerCase();
  const rule = SOCIAL_RULES[platform];
  const hasProtocol = /^https?:\/\//i.test(sanitized);

  const foundDomain = ALL_PLATFORM_DOMAINS.find((d) => {
    if (hasProtocol) {
      return lower.includes(`://${d}`) || lower.includes(`://www.${d}`);
    }
    return lower.startsWith(d) || lower.startsWith(`www.${d}`);
  });

  if (foundDomain) {
    const withProtocol = hasProtocol ? sanitized : `https://${sanitized}`;
    let url: URL;
    try {
      url = new URL(withProtocol);
    } catch {
      return { ok: false, error: 'Invalid URL' };
    }

    const hostname = url.hostname.toLowerCase();

    if (!hostnameMatchesPlatform(hostname, platform)) {
      const wrongPlatform = findPlatformForHostname(hostname);
      if (wrongPlatform) {
        return {
          ok: false,
          error: `This is a ${wrongPlatform} URL, not a ${platform} URL`,
        };
      }
      return { ok: false, error: `URL must be a ${platform} link` };
    }

    const isShortDomain = rule.shortDomains.some((d) => hostname === d || hostname === `www.${d}`);
    if (isShortDomain) {
      return { ok: true, url: `https://${hostname}${url.pathname}${url.search}` };
    }
    return { ok: true, url: `https://${rule.canonicalDomain}${url.pathname}${url.search}` };
  }

  if (hasProtocol) {
    return { ok: false, error: `URL must be a ${platform} link` };
  }

  if (/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?\.[a-z]{2,}\//i.test(sanitized)) {
    return { ok: false, error: `URL must be a ${platform} link` };
  }

  const username = sanitized.replace(/^@/, '');
  if (!username) {
    return { ok: false, error: 'Username cannot be empty' };
  }

  return {
    ok: true,
    url: `https://${rule.canonicalDomain}${rule.bareUsernamePrefix}${username}`,
  };
}

export function normalizeSocialLinks(input: Record<string, string>): {
  normalized: Record<string, string>;
  errors: Record<string, string>;
} {
  const normalized: Record<string, string> = {};
  const errors: Record<string, string> = {};

  for (const [key, value] of Object.entries(input)) {
    if (!value || !value.trim()) continue;

    if (!SOCIAL_PLATFORM_KEYS.includes(key as SocialPlatform)) {
      errors[key] = `Unknown platform: ${key}`;
      continue;
    }

    const result = normalizeSocialUrl(key as SocialPlatform, value);
    if (result.ok) {
      normalized[key] = result.url;
    } else {
      errors[key] = result.error;
    }
  }

  return { normalized, errors };
}
